<script setup lang="ts">
const RIGHT_IMG = 'https://resources1.interface003.com/web30/2026/06/11/f4122ad0-2700-484d-85e5-7fc2ce51220a.png'
const LEFT_IMG = 'https://resources1.interface003.com/web30/2026/06/11/b20f9767-20ee-4d76-b98f-927eb724c8f6.png'
const CLOSE_IMG = 'https://resources1.interface003.com/web30/2026/06/24/79597aa8-cc8a-4b81-a642-c0579965981e.png'
/** 组件入参，控制图片弹窗显隐、图片列表和初始索引。 */
const props = withDefaults(
  defineProps<{
    modelValue: boolean
    images?: string[]
    initialIndex?: number
  }>(),
  {
    images: () => [],
    initialIndex: 0,
  },
)

/** 向父组件同步弹窗显隐状态。 */
const emit = defineEmits<{
  'update:modelValue': [value: boolean]
}>()

/** 双向绑定后的弹窗显示状态。 */
const visible = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit('update:modelValue', value),
})

/** 本地缓存的有效图片列表，再次接收入参时先清空旧值。 */
const imageList = ref<string[]>([])

/** 当前正在预览的图片索引。 */
const currentIndex = ref(0)
/** 图片切换方向，用于控制轮播进入/离场动画。 */
const transitionDirection = ref<'prev' | 'next'>('next')
/** 当前预览图片是否仍在加载。 */
const isImageLoading = ref(false)

/**
 * 将任意索引归一化到图片列表范围内。
 * @param index 原始索引，支持负数和越界值。
 * @param total 图片总数。
 * @returns 映射后的有效索引。
 */
const normalizedIndex = (index: number, total: number) => {
  if (total <= 0) {
    return 0
  }

  return ((index % total) + total) % total
}

watch(
  () => props.images,
  (images) => {
    imageList.value = []
    imageList.value = (images || []).filter(Boolean)
  },
  {
    immediate: true,
    deep: true,
  },
)

watch(
  () => [props.initialIndex, imageList.value.length],
  ([index, total]) => {
    currentIndex.value = normalizedIndex(Number(index) || 0, total || 0)
  },
  {
    immediate: true,
  },
)

/** 当前大图地址，越界时返回空字符串。 */
const currentImage = computed(() => imageList.value[currentIndex.value] || '')

/** 是否存在多张图片，用于决定是否展示切换控件。 */
const hasMultipleImages = computed(() => imageList.value.length > 1)

onMounted(() => {
  window.addEventListener('keydown', handleKeydown)
})

onBeforeUnmount(() => {
  window.removeEventListener('keydown', handleKeydown)
})

/**
 * 按步长切换当前图片。
 * @param step 切换步长，正数向后，负数向前。
 * @returns 无返回值。
 */
const switchImage = (step: number) => {
  if (!hasMultipleImages.value) {
    return
  }

  isImageLoading.value = true
  transitionDirection.value = step < 0 ? 'prev' : 'next'
  currentIndex.value = normalizedIndex(currentIndex.value + step, imageList.value.length)
}

/** 切换到上一张图片。 */
const prevImage = () => switchImage(-1)
/** 切换到下一张图片。 */
const nextImage = () => switchImage(1)

/** 当前图片加载结束后关闭 loading。 */
const finishImageLoading = () => {
  isImageLoading.value = false
}

/**
 * 弹窗显示时响应键盘左右方向键。
 * @param event 键盘事件。
 */
function handleKeydown(event: KeyboardEvent) {
  if (!visible.value || !hasMultipleImages.value) {
    return
  }

  if (event.key === 'ArrowLeft') {
    event.preventDefault()
    prevImage()
  } else if (event.key === 'ArrowRight') {
    event.preventDefault()
    nextImage()
  }
}

/** 关闭图片弹窗。 */
const closeDialog = () => {
  visible.value = false
}
</script>

<template>
  <ClientOnly>
    <ElDialog
      v-model="visible"
      width="1000px"
      class="xy-fixed !m-0 h-[568px] !rounded-[32px] !bg-[transparent] !p-0"
      body-class="!p-0"
      header-class="hidden"
      :close-on-click-modal="false"
    >
      <div class="relative isolate">
        <div class="relative h-[568px]">
          <Transition :name="transitionDirection === 'prev' ? 'survey-img-prev' : 'survey-img-next'">
            <CommonImage
              v-if="currentImage"
              :key="currentImage"
              :src="currentImage"
              alt="survey image"
              class="absolute inset-0 block size-full rounded-[32px] object-cover"
              loading="eager"
              @load="finishImageLoading"
              @error="finishImageLoading"
            />
            <div
              v-else
              key="empty"
              class="absolute inset-0 flex size-full items-center justify-center bg-[#f5f6fa] text-[14px] leading-[22px] text-[#7d818a]"
            >
              --
            </div>
          </Transition>

          <div
            v-if="isImageLoading && currentImage"
            class="pointer-events-none absolute inset-0 z-[2] flex items-center justify-center"
          >
            <div class="image-loading-spinner size-[40px]" />
          </div>

          <button
            type="button"
            class="absolute top-[32px] right-[32px] z-[3] flex size-[32px] cursor-pointer items-center justify-center rounded-full bg-[rgba(5,12,31,0.46)] hover:bg-[rgba(5,12,31,0.62)] rtl:right-auto rtl:left-[32px]"
            @click="closeDialog"
          >
            <CommonImage class="size-[12px]" :src="CLOSE_IMG" alt="close" loading="lazy" />
          </button>

          <template v-if="hasMultipleImages">
            <button
              type="button"
              class="absolute top-1/2 left-[20px] z-[2] flex size-[40px] -translate-y-1/2 cursor-pointer items-center justify-center rounded-[12px] bg-[rgba(5,12,31,0.46)] text-[20px] leading-[20px] text-white hover:bg-[rgba(5,12,31,0.62)] rtl:right-[20px] rtl:left-auto rtl:rotate-180"
              @click="prevImage"
            >
              <CommonImage :src="LEFT_IMG" alt="prev" class="size-[20px]" loading="lazy" />
            </button>

            <button
              type="button"
              class="absolute top-1/2 right-[20px] z-[2] flex size-[40px] -translate-y-1/2 cursor-pointer items-center justify-center rounded-[12px] bg-[rgba(5,12,31,0.46)] text-[20px] leading-[20px] text-white hover:bg-[rgba(5,12,31,0.62)] rtl:right-auto rtl:left-[20px] rtl:rotate-180"
              @click="nextImage"
            >
              <CommonImage :src="RIGHT_IMG" alt="next" class="size-[20px]" />
            </button>

            <div
              class="absolute right-[20px] bottom-[20px] z-[2] min-w-[54px] rounded-[999px] bg-[rgba(5,12,31,0.46)] px-[16px] py-[6px] text-[14px] leading-[20px] text-white rtl:right-auto rtl:left-[20px]"
            >
              {{ currentIndex + 1 }}/{{ imageList.length }}
            </div>
          </template>
        </div>
      </div>
    </ElDialog>
  </ClientOnly>
</template>

<style scoped lang="less">
.image-loading-spinner {
  border: 4px solid rgba(255, 255, 255, 0.35);
  border-top-color: #fff;
  border-radius: 50%;
  animation: image-loading-spin 0.8s linear infinite;
}

@keyframes image-loading-spin {
  0% {
    transform: rotate(0);
  }

  100% {
    transform: rotate(360deg);
  }
}
</style>
